import React from 'react';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { Dialog, DialogContent, Tooltip } from '@mui/material';
import { formatDateTime } from '../utils';
import { Badge } from './ui/Badge';

interface Courier {
  id: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  avatarUrl?: string;
  courierType?: string;
  city?: {
    id: string;
    name: string;
  };
}

interface Shift {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  shiftTemplate?: {
    id: string;
    name: string;
  } | null;
}

interface ShiftHistoryItem {
  id: string;
  shiftId: string;
  courierId: string;
  onlineHours: number;
  busyHours: number;
  idleHours: number;
  onlineSeconds: number;
  busySeconds: number;
  idleSeconds: number;
  onlinePercentage: number;
  acceptanceRate: number;
  acceptanceRateCompliant: boolean;
  onlineHoursCompliant: boolean;
  compliant: boolean;
  notificationsReceived: number;
  ordersAccepted: number;
  ordersRejected: number;
  createdAt: string;
  updatedAt: string;
  courier: Courier;
  shift: Shift;
}

interface ComplianceReportDetailsModalProps {
  open: boolean;
  report: ShiftHistoryItem | null;
  onClose: () => void;
}

export const ComplianceReportDetailsModal: React.FC<ComplianceReportDetailsModalProps> = ({
  open,
  report,
  onClose,
}) => {
  if (!report) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        maxWidth={false}
      >
        <DialogContent
          sx={{
            padding: 0,
            backgroundColor: '#ffffff',
            overflow: 'auto',
          }}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
            <div className="mb-6">
              <button
                onClick={onClose}
                className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors"
              >
                <ArrowBackIcon sx={{ fontSize: '20px' }} />
                <span className="text-sm font-medium">Back</span>
              </button>
            </div>
            <p className="text-gray-600">Report not found</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth={false}
    >
      <DialogContent
        sx={{
          padding: 0,
          backgroundColor: '#ffffff',
          overflow: 'auto',
        }}
      >
        <div className="flex flex-col space-y-2 bg-white">
          <div className="main-wrapper">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="mb-6">
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors group"
                >
                  <ArrowBackIcon
                    sx={{ fontSize: '20px' }}
                    className="group-hover:translate-x-[-2px] transition-transform"
                  />
                  <span className="text-sm font-medium">Back</span>
                </button>
              </div>
              <div className="mb-8">
                <div className={`bg-gradient-to-r rounded-2xl p-8 shadow-xl relative overflow-hidden ${report.compliant
                  ? 'from-green-500 via-green-600 to-green-700'
                  : 'from-red-500 via-red-600 to-red-700'
                  }`}>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative z-10">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                        <AssessmentIcon sx={{ fontSize: '32px', color: 'white' }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h1 className="text-3xl font-bold text-white">Shift Driver Stat Details</h1>
                          <Badge variant={report.compliant ? 'success' : 'destructive'}>
                            {report.compliant ? 'Compliant' : 'Non-Compliant'}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-base text-white/90 leading-relaxed">
                            <span className="font-semibold">Compliance ID:</span> <span className="font-mono">{report.id}</span>
                          </p>
                          <p className="text-base text-white/90 leading-relaxed">
                            <span className="font-semibold">Courier ID:</span> <span className="font-mono">{report.courierId}</span> | <span className="font-semibold">Shift Session ID:</span> <span className="font-mono">{report.shiftId}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 space-y-6">
                {/* IDs Section */}
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 p-6">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-4 block">Identifiers</span>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Compliance Report ID</span>
                      <p className="text-sm font-bold text-gray-900 mt-1 font-mono break-all">{report.id}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Courier ID</span>
                      <p className="text-sm font-bold text-gray-900 mt-1 font-mono break-all">{report.courierId}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Shift Session ID</span>
                      <p className="text-sm font-bold text-gray-900 mt-1 font-mono break-all">{report.shiftId}</p>
                    </div>
                  </div>
                </div>

                {/* Courier Information */}
                {report.courier && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 pb-6 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-4 block">Courier Information</span>
                    <div className="flex items-start gap-4">
                      {report.courier.avatarUrl ? (
                        <img
                          src={report.courier.avatarUrl}
                          alt={report.courier.firstName || 'Driver'}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center text-white font-semibold text-lg border-2 border-gray-200 shadow-sm flex-shrink-0">
                          {(report.courier.firstName || 'D').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Name</span>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-base font-semibold text-gray-900">
                              {report.courier.firstName} {report.courier.lastName}
                            </p>
                            {report.courier.courierType && (
                              <Tooltip title={`Courier Type: ${report.courier.courierType}`} arrow>
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium border border-blue-200">
                                  <BadgeIcon sx={{ fontSize: '12px' }} />
                                  {report.courier.courierType}
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Courier ID</span>
                          <p className="text-sm font-medium text-gray-900 mt-1 font-mono">{report.courier.id}</p>
                        </div>
                        {report.courier.mobileNumber && (
                          <div>
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Mobile Number</span>
                            <div className="flex items-center gap-2 mt-1">
                              <PhoneIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                              <p className="text-sm font-medium text-gray-900">{report.courier.mobileNumber}</p>
                            </div>
                          </div>
                        )}
                        {report.courier.city && (
                          <div>
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">City</span>
                            <div className="flex items-center gap-2 mt-1">
                              <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                              <p className="text-sm font-medium text-gray-900">{report.courier.city.name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Time Information */}
                <div className="grid grid-cols-3 gap-4 pb-6 border-b border-gray-200">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Online Hours</span>
                    <p className="text-lg font-bold text-gray-900 mt-1">{report.onlineHours.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-1">{report.onlineSeconds} seconds</p>
                    {report.onlineHoursCompliant && (
                      <span className="text-xs text-green-600 mt-1 block">✓ Compliant</span>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Busy Hours</span>
                    <p className="text-lg font-bold text-gray-900 mt-1">{report.busyHours.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-1">{report.busySeconds} seconds</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Idle Hours</span>
                    <p className="text-lg font-bold text-gray-900 mt-1">{report.idleHours.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-1">{report.idleSeconds} seconds</p>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-2 gap-4 pb-6 border-b border-gray-200">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Online Percentage</span>
                    <p className={`text-lg font-bold mt-1 ${report.onlinePercentage >= 95 ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {report.onlinePercentage.toFixed(2)}%
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Acceptance Rate</span>
                    <p className={`text-lg font-bold mt-1 ${report.acceptanceRate >= 90 ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {report.acceptanceRate.toFixed(2)}%
                    </p>
                    {report.acceptanceRateCompliant && (
                      <span className="text-xs text-green-600 mt-1 block">✓ Compliant</span>
                    )}
                  </div>
                </div>

                {/* Compliance Status */}
                <div className="pb-6 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Compliance Status</span>
                  <div className="mt-2 flex items-center gap-3">
                    <Badge variant={report.compliant ? 'success' : 'destructive'}>
                      {report.compliant ? 'Compliant' : 'Non-Compliant'}
                    </Badge>
                    {!report.compliant && (
                      <div className="text-sm text-red-600">
                        {!report.onlineHoursCompliant && <span className="mr-3">✗ Online Hours</span>}
                        {!report.acceptanceRateCompliant && <span>✗ Acceptance Rate</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Activity */}
                <div className="grid grid-cols-3 gap-4 pb-6 border-b border-gray-200">
                  <div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Notifications Received</span>
                    <p className="text-lg font-bold text-gray-900 mt-1">{report.notificationsReceived}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Orders Accepted</span>
                    <p className="text-lg font-bold text-green-600 mt-1">{report.ordersAccepted}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Orders Rejected</span>
                    <p className="text-lg font-bold text-red-600 mt-1">{report.ordersRejected}</p>
                  </div>
                </div>

                {/* Shift Session Information */}
                <div className="pb-6 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 block">Shift Session Information</span>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Shift Session ID</span>
                      <p className="text-sm font-medium text-gray-900 mt-1 font-mono">{report.shift.id}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Status</span>
                      <p className={`text-sm font-medium mt-1 ${report.shift.status === 'COMPLETED' ? 'text-green-600' :
                        report.shift.status === 'ACTIVE' ? 'text-blue-600' :
                          report.shift.status === 'CANCELLED' ? 'text-red-600' :
                            'text-gray-600'
                        }`}>
                        {report.shift.status}
                      </p>
                    </div>
                    {report.shift.shiftTemplate && (
                      <>
                        <div>
                          <span className="text-sm text-gray-600">Shift Schedule ID</span>
                          <p className="text-sm font-medium text-gray-900 mt-1 font-mono">{report.shift.shiftTemplate.id}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Shift Schedule Name</span>
                          <p className="text-sm font-medium text-gray-900 mt-1">{report.shift.shiftTemplate.name || 'N/A'}</p>
                        </div>
                      </>
                    )}
                    <div>
                      <span className="text-sm text-gray-600">Start Time</span>
                      <p className="text-sm font-medium text-gray-900 mt-1">{formatDateTime(report.shift.startTime)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">End Time</span>
                      <p className="text-sm font-medium text-gray-900 mt-1">{formatDateTime(report.shift.endTime)}</p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Created At</span>
                    <p className="text-sm font-medium text-gray-900 mt-1">{formatDateTime(report.createdAt)}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Updated At</span>
                    <p className="text-sm font-medium text-gray-900 mt-1">{formatDateTime(report.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

